/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { HomeEvent, type HomeSectionKey } from '@/constants/analytics';
import type { AnalyticsItemClick } from '@/context/analytics-item/interfaces';
import { track } from '@/utils/analytics';

interface HomeAnalyticsContextProps {
  visit: number;
  markSectionViewed: (section: HomeSectionKey, position: number) => void;
  trackItemClick: (
    section: HomeSectionKey,
    sectionPosition: number,
    click: AnalyticsItemClick
  ) => void;
}

interface HomeAnalyticsProviderProps {
  sectionsAvailable: number;
  children: ReactNode;
}

export const HomeAnalyticsContext =
  createContext<HomeAnalyticsContextProps | null>(null);

export const HomeAnalyticsProvider = ({
  sectionsAvailable,
  children,
}: HomeAnalyticsProviderProps) => {
  const startedAtRef = useRef(Date.now());
  const sectionsViewedRef = useRef(new Map<HomeSectionKey, number>());
  const sectionsAvailableRef = useRef(sectionsAvailable);
  const sentRef = useRef(false);
  const [visit, setVisit] = useState(0);

  useEffect(() => {
    sectionsAvailableRef.current = sectionsAvailable;
  }, [sectionsAvailable]);

  const sendVisitEnded = useCallback((beforeUnload: boolean) => {
    if (sentRef.current) return;
    sentRef.current = true;

    const sectionsViewed = [...sectionsViewedRef.current.entries()].sort(
      ([, positionA], [, positionB]) => positionA - positionB
    );

    track(
      HomeEvent.VisitEnded,
      {
        sections_viewed: sectionsViewed.map(([section]) => section),
        deepest_position: sectionsViewed[sectionsViewed.length - 1]?.[1] ?? 0,
        sections_available: sectionsAvailableRef.current,
        duration_seconds: Math.round(
          (Date.now() - startedAtRef.current) / 1000
        ),
      },
      { beforeUnload }
    );
  }, []);

  useEffect(() => {
    const handlePageHide = () => sendVisitEnded(true);
    // Coming back from the back-forward cache restores the Home without remounting it.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      sentRef.current = false;
      startedAtRef.current = Date.now();
      sectionsViewedRef.current.clear();
      setVisit((current) => current + 1);
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      sendVisitEnded(false);
    };
  }, [sendVisitEnded]);

  const markSectionViewed = useCallback(
    (section: HomeSectionKey, position: number) => {
      if (!sectionsViewedRef.current.has(section)) {
        sectionsViewedRef.current.set(section, position);
      }
    },
    []
  );

  const trackItemClick = useCallback(
    (
      section: HomeSectionKey,
      sectionPosition: number,
      { type, id, position }: AnalyticsItemClick
    ) => {
      track(HomeEvent.ItemClicked, {
        section,
        section_position: sectionPosition,
        item_type: type,
        item_id: id,
        position,
      });
    },
    []
  );

  // A new value per visit makes the sections observe the viewport again.
  const value = useMemo(
    () => ({ visit, markSectionViewed, trackItemClick }),
    [visit, markSectionViewed, trackItemClick]
  );

  return (
    <HomeAnalyticsContext.Provider value={value}>
      {children}
    </HomeAnalyticsContext.Provider>
  );
};

export const useHomeAnalytics = () => useContext(HomeAnalyticsContext);
